<template>
  <div id="header" class="header" :class="headerClasses">
    <header-logo />

    <div class="header-content">
      <div class="pull-right">
        <locale-switcher :current-locale="locale" />
      </div>
      <div v-if="permissions.is_assembler" class="pull-right header-catalog">
        <a href="/static/st-service/Handbuch_EZB.pdf" target="_blank" rel="noopener">
          <img :src="catalogBanner" :alt="$t('vue.components.header.download_catalog')">
        </a>
      </div>

      <h2 class="welcome-msg" v-html="welcomeMessage"></h2>

      <div id="user" class="header-user-name">
        <span class="user-fullname">
          {{ user.fullname }}
        </span>
        <span v-if="trueUser" class="true-user-fullname">
          ({{ trueUser.fullname }})
        </span>
      </div>

      <nav class="nav-container" :aria-label="$t('vue.components.header.main_navigation')">
        <ul id="navigation" class="nav-main nav nav-tabs">
          <!-- Overview -->
          <li v-if="permissions.can_index_projects" role="presentation" :class="{ active: isProjectsIndex }">
            <router-link to="/projects">
              <span class="glyphicon glyphicon-home" aria-hidden="true"></span>
              <span class="hidden-xs nav-label" :class="{ 'hidden-sm hidden-md': hasProject }">
                {{ $t('vue.components.header.overview') }}
              </span>
            </router-link>
          </li>

          <!-- Project-specific navigation -->
          <template v-if="hasProject">
            <!-- Details -->
            <li v-if="canShowDetails" role="presentation" :class="{ active: isProjectDetail }">
              <router-link :to="`/projects/${project.id}`">
                <span class="glyphicon glyphicon-info-sign" aria-hidden="true"></span>
                <span class="hidden-xs hidden-sm nav-label">{{ $t('vue.components.header.details') }}</span>
              </router-link>
            </li>

            <!-- Line Items dropdown -->
            <li v-if="hasPhases" role="presentation" class="dropdown" :class="{ active: isLineItems }">
              <a href="#" id="line-items-dropdown" class="dropdown-toggle" v-dropdown>
                <span class="glyphicon glyphicon-list" aria-hidden="true"></span>
                <span class="hidden-xs hidden-sm nav-label">{{ $t('vue.components.header.line_items') }}</span>
                <span class="caret"></span>
              </a>
              <ul class="dropdown-menu nav nav-stacked dropdown-filters" aria-labelledby="line-items-dropdown">
                <li v-for="phase in visiblePhases" :key="phase.id" role="presentation">
                  <router-link
                    :to="`/projects/${project.id}/phases/${phase.id}/line_items`"
                    class="filter-link"
                  >
                    <div class="filter-link-text">
                      {{ phase.title }} {{ phase.identifier }}
                    </div>
                  </router-link>
                </li>
              </ul>
            </li>

            <!-- Tasks -->
            <li v-if="permissions.can_index_tasks" role="presentation" :class="{ active: isProjectTasks }">
              <router-link :to="`/projects/${project.id}/tasks`">
                <span class="glyphicon glyphicon-check" aria-hidden="true"></span>
                <span class="hidden-xs hidden-sm nav-label">{{ $t('vue.components.header.tasks') }}</span>
                <span v-if="project.task_progress" class="badge hidden-xs">
                  {{ project.task_progress }}
                </span>
              </router-link>
            </li>

            <!-- Messages -->
            <li role="presentation" class="dropdown" :class="{ active: isProjectMessages }">
              <template v-if="permissions.is_admin">
                <a href="#" id="messages-dropdown" class="dropdown-toggle" v-dropdown @click="fetchMessageCounts">
                  <span class="glyphicon glyphicon-comment" aria-hidden="true"></span>
                  <span class="hidden-xs hidden-sm nav-label">{{ $t('vue.components.header.messages') }}</span>
                  <span class="caret"></span>
                </a>
                <ul class="dropdown-menu nav nav-stacked dropdown-filters" aria-labelledby="messages-dropdown">
                  <li v-for="msgType in messageTypes" :key="msgType" role="presentation">
                    <router-link :to="`/projects/${project.id}/${msgType}s`" class="filter-link">
                      <div class="filter-link-text">{{ $t(`vue.components.header.message_types.${msgType}`) }}</div>
                      <div class="filter-count-wrapper">
                        <span v-if="messageCounts[msgType]" class="badge filter-badge">
                          {{ messageCounts[msgType] }}
                        </span>
                      </div>
                    </router-link>
                  </li>
                </ul>
              </template>
              <template v-else>
                <router-link :to="`/projects/${project.id}/${defaultMessageType}s`">
                  <span class="glyphicon glyphicon-comment" aria-hidden="true"></span>
                  <span class="hidden-xs hidden-sm nav-label">{{ $t('vue.components.header.messages') }}</span>
                </router-link>
              </template>
            </li>

            <!-- Files -->
            <li role="presentation" :class="{ active: isProjectFolders }">
              <router-link :to="`/projects/${project.id}/folders`">
                <span class="glyphicon glyphicon-file" aria-hidden="true"></span>
                <span class="hidden-xs hidden-sm nav-label">{{ $t('vue.components.header.files') }}</span>
              </router-link>
            </li>
          </template>
        </ul>

        <!-- Account menu (desktop) -->
        <account-menu :user="user" :permissions="permissions" :simulating="simulating" :session-links="sessionLinks" :burgermenu="false" />

        <!-- Burger menu (mobile) -->
        <ul id="burgermenu" class="nav-account nav nav-tabs visible-xs visible-sm visible-md">
          <li role="presentation" class="dropdown">
            <a id="burgermenu-dropdown" class="dropdown-toggle" type="button" v-dropdown>
              <span class="sr-only">{{ $t('vue.components.header.menu') }}</span>
              <span class="glyphicon glyphicon-menu-hamburger" aria-hidden="true"></span>
            </a>
            <account-menu :user="user" :permissions="permissions" :simulating="simulating" :session-links="sessionLinks" :burgermenu="true" />
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import { progressFetch } from '@/helpers/progress-fetch.js'
import { apiUrl } from '@/helpers/api.js'
import LocaleSwitcher from '@/layouts/locale_switcher.vue'
import AccountMenu from '@/layouts/account_menu.vue'
import HeaderLogo from '@/layouts/header_logo.vue'
import catalogBanner from '@assets/images/catalog_banner.jpg'

export default {
  name: 'app-header',

  props: {
    user: { type: Object, required: true },
    trueUser: { type: Object, default: null },
    simulating: { type: Boolean, default: false },
    sessionLinks: { type: Object, default: () => ({}) },
    permissions: { type: Object, required: true },
    locale: { type: String, required: true }
  },

  data() {
    return {
      messageCounts: {},
      catalogBanner
    }
  },

  created() {
    this.ensureProjectLoaded()
  },

  watch: {
    '$route': 'ensureProjectLoaded'
  },

  methods: {
    ensureProjectLoaded() {
      const projectId = this.$route.params.projectId || (this.$route.name === 'project-show' ? this.$route.params.id : null)
      const isNewProject = this.$route.name === 'project-new'

      // Clear project when navigating away from project routes
      if (!projectId && !isNewProject) {
        if (this.$store.state.project.project) {
          this.$store.commit('project/resetState')
        }
        return
      }

      // Skip if already loading
      if (this.$store.state.project.loading) return

      // For new projects, fetch template
      if (isNewProject) {
        const currentProject = this.$store.state.project.project
        if (currentProject && !currentProject.persisted) return // Already have new project template
        const partnerId = this.$route.query.partner_id ? Number(this.$route.query.partner_id) : null
        this.$store.dispatch('project/fetchProject', { partnerId })
        return
      }

      // Skip if project already loaded with correct id
      const currentProject = this.$store.state.project.project
      if (currentProject && String(currentProject.id) === String(projectId)) return

      this.$store.dispatch('project/fetchProject', { id: projectId })
    },

    async fetchMessageCounts(event) {
      if (!this.project?.id) return

      // Only fetch when opening (dropdown doesn't have 'open' class yet)
      const dropdown = event.target.closest('.dropdown')
      if (dropdown?.classList.contains('open')) return

      try {
        const response = await progressFetch(apiUrl(`/projects/${this.project.id}/messages/count`), {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        })
        this.messageCounts = response.ok ? await response.json() : {}
      } catch {
        this.messageCounts = {}
      }
    }
  },

  computed: {
    project() {
      return this.$store.state.project.project
    },

    headerClasses() {
      return {
        'no-nav': !this.user,
        'assembler': this.permissions.is_assembler
      }
    },

    welcomeMessage() {
      return `${this.$t('vue.components.header.welcome_html')} <abbr title="${this.$t('vue.components.header.wois_full')}">W.O.I.S.</abbr>`
    },

    hasProject() {
      return this.project?.id
    },

    canShowDetails() {
      return !this.permissions.is_guest
    },

    hasPhases() {
      return this.project?.phases?.length > 0
    },

    visiblePhases() {
      if (!this.project?.phases) return []
      return this.project.phases.filter(phase => !phase.internal)
    },

    messageTypes() {
      return ['admin_internal_message', 'customer_message', 'assembler_message', 'quote_message', 'assembly_order_message']
    },

    defaultMessageType() {
      if (this.permissions.is_assembler) return 'assembler_message'
      if (this.permissions.is_customer || this.permissions.is_guest) return 'customer_message'
      return 'admin_internal_message'
    },

    isProjectsIndex() {
      return this.$route.name === 'projects'
    },

    isProjectDetail() {
      return this.$route.name === 'project-show' || this.$route.name === 'project-edit'
    },

    isLineItems() {
      return this.$route.name === 'line-items'
    },

    isProjectTasks() {
      return this.$route.name === 'project-tasks' ||
             this.$route.name === 'project-task-new' ||
             this.$route.name === 'project-task-show'
    },

    isProjectMessages() {
      return this.$route.name?.includes('messages') && this.$route.params.projectId
    },

    isProjectFolders() {
      return this.$route.name === 'project-folders'
    }
  },

  components: {
    LocaleSwitcher,
    AccountMenu,
    HeaderLogo
  }
}
</script>

<style lang="scss" scoped>
@use "variables";

.header {
  position: relative;
  height: variables.$logo-height-small + variables.$nav-container-height;
  background-color: variables.$header-background-color;

  &.no-nav {
    height: variables.$logo-height-small;
  }

  @media screen and (min-width: variables.$screen-sm) {
    &,
    &.no-nav {
      height: variables.$logo-height;
    }
  }

  .locale-switcher {
    margin-right: variables.$container-padding;
    margin-left: variables.$container-padding;
  }

  .header-content {
    padding-top: variables.$container-padding;
    color: variables.$white;

    .welcome-msg {
      font-size: 16px;
      margin-top: 0;
    }

    .header-catalog {
      display: none;

      @media screen and (min-width: variables.$screen-md) {
        display: block;
        margin-left: 15px;

        img {
          height: 105px;
          width: auto;
        }
      }
    }
  }

  .nav-main,
  :deep(.nav-account) {
    position: absolute;

    > li > a {
      border: none !important;
      background-color: variables.$button-background-color;
      color: variables.$white;
    }

    > li > a:hover {
      background-color: variables.$button-hover-color;
      color: variables.$white;
      cursor: pointer;
    }

    > li.active > a,
    > li.active:hover > a,
    .dropdown.open .dropdown-toggle {
      background-color: variables.$white;
      color: variables.$font-color;
    }

    > li > a > .badge {
      background-color: variables.$white;
      color: variables.$button-hover-color;
    }

    > li.active > a > .badge {
      background-color: variables.$text-color;
      color: variables.$white;
    }
  }

  .nav-main {
    left: 0;
  }

  :deep(.nav-account) {
    right: 0;

    .logout a {
      margin-right: 0;
    }

    @media screen and (min-width: variables.$screen-sm) {
      right: variables.$container-padding;
    }

    > li {
      float: right;
      margin-left: 4px;
    }
  }

  .nav-container {
    height: variables.$nav-container-height;
    position: absolute;
    background: variables.$button-background-color;
    left: 0;
    right: 0;
    bottom: 0;

    @media screen and (min-width: variables.$screen-sm) {
      left: variables.$logo-width + variables.$container-padding;
      background: transparent;

      ul > li.active {
        .nav-label {
          display: inline !important;
        }
      }
    }
  }

}

.dropdown-menu-right-align {
  right: 1px;
  left: auto;
}
</style>
